import type {
    DivisionConfig,
    FlightConfig,
    PopulatedFlight,
    RegistrationLike,
} from "./flightPlacementTypes";

export interface PlaceRegistrationsManualParams {
    /** All registrations to place. */
    registrations: RegistrationLike[];
    /** Division definitions (for resolving division by name). */
    divisions: DivisionConfig[];
    /** Effective flights (used to find flight index when not useDivisionsAsFlights). */
    effectiveFlights: FlightConfig[];
    /** Populated flights to mutate (we push players into .players). */
    populatedFlights: PopulatedFlight[];
    /** If true, each division is one flight; match by division index only. */
    useDivisionsAsFlights: boolean;
}

/**
 * Places registrations into flights using manual assignment.
 *
 * LOGIC (manual mode):
 * - Each registration has division (or divisionName) and optionally flight (or flightName).
 * - We resolve the division by name, then:
 *   - If useDivisionsAsFlights: there is one flight per division, so we find the
 *     populated flight by divisionIndex and push the registration there.
 *   - Else: we also need the flight name from the registration; we find the
 *     matching flight in effectiveFlights for that division, then find the
 *     corresponding populated flight by index and push there.
 * - Registrations without a division (or with an unknown division) are skipped.
 * - In non–divisions-as-flights mode, registrations without a flight are skipped.
 */
export function placeRegistrationsManual(params: PlaceRegistrationsManualParams): void {
    const {
        registrations,
        divisions,
        effectiveFlights,
        populatedFlights,
        useDivisionsAsFlights,
    } = params;

    registrations.forEach((reg: RegistrationLike) => {
        const divName = reg.division ?? reg.divisionName;
        if (divName == null) return;

        const division = divisions.find(
            (d: DivisionConfig) =>
                (d.divisionName ?? "").toString() === (divName ?? "").toString()
        );
        if (division == null) return;

        if (useDivisionsAsFlights) {
            const popFlight = populatedFlights.find(
                (p: PopulatedFlight) => p.divisionIndex === division.index
            );
            if (popFlight) popFlight.players.push(reg);
        } else {
            const flightName = reg.flight ?? reg.flightName;
            if (flightName == null) return;

            const flight = effectiveFlights.find(
                (f: FlightConfig) =>
                    f.divisionIndex === division.index &&
                    (f.flightName ?? "").toString() === (flightName ?? "").toString()
            );
            if (flight == null) return;

            const popFlight = populatedFlights.find((p: PopulatedFlight) => p.index === flight.index);
            if (popFlight) popFlight.players.push(reg);
        }
    });
}
